import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FormGroup, Input, Label } from 'reactstrap';
import chevronDown from 'assets/chevron-down.svg';
import chevronUp from 'assets/chevron-up.svg';
import NumberInputField from 'components/layout/forms/NumberInputField';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import CheckboxInputField from 'components/layout/forms/CheckboxInputField';
import translations from 'services/translations/translations';

export type WoodyMasterFieldType = 'number' | 'select' | 'checkbox';

export interface WoodyVariant {
  name: string;
  label: string;
  value: number | boolean;
  error?: string;
}

const WoodyMasterField = ({
  masterLabel,
  variants,
  type,
  options,
  onChange,
  disabled,
}: {
  masterLabel: string;
  variants: WoodyVariant[];
  type: WoodyMasterFieldType;
  options?: any;
  onChange: (event: {
    target: { name: string; value?: any; checked?: boolean };
  }) => void;
  disabled?: boolean;
}): JSX.Element => {
  const { t } = useTranslation([translations.woodyWallProperties]);
  const mixedLabel = t(`${translations.woodyWallProperties}:mixed`);
  const [expanded, setExpanded] = useState(false);
  const masterId = `${variants
    .map((variant) => variant.name)
    .join('-')}-master`;

  const values = variants.map((variant) => variant.value);
  const allSame = values.every((value) => value === values[0]);
  // The variants disagree. Surface this clearly on the master input instead
  // of just blanking it out, so it is obvious the field is hiding a mix of
  // per-side values rather than being empty.
  const mixed = !allSame;

  const masterError = variants
    .map((variant) => variant.error)
    .find((error) => error !== undefined);

  const masterLabelNode = (
    <>
      {masterLabel}
      {mixed ? (
        <span className="woody-master-field-mixed-badge">{mixedLabel}</span>
      ) : null}
    </>
  );

  // Apply a single change to every variant at once. The dispatches are
  // batched so React re-renders once with every variant already updated,
  // instead of flashing through intermediate states where the variants
  // disagree and the master would momentarily blank out.
  const broadcast = (payload: { value?: any; checked?: boolean }) => {
    batchedUpdates(() => {
      variants.forEach((variant) => {
        onChange({ target: { name: variant.name, ...payload } });
      });
    });
  };

  const renderMasterInput = () => {
    if (type === 'checkbox') {
      return (
        <MasterCheckbox
          id={masterId}
          label={masterLabelNode}
          checked={allSame ? Boolean(values[0]) : false}
          indeterminate={mixed}
          disabled={disabled}
          onChange={(checked) => broadcast({ checked })}
        />
      );
    }

    if (type === 'select') {
      return (
        <FormGroup>
          <Label className="label" for={masterId}>
            {masterLabelNode}
          </Label>
          <Input
            className={`text-field${mixed ? ' text-field--mixed' : ''}`}
            id={masterId}
            type="select"
            value={allSame ? (values[0] as any) : ''}
            disabled={disabled}
            onChange={(event) => broadcast({ value: event.target.value })}
          >
            {allSame ? null : <option value="">{mixedLabel}</option>}
            {options}
          </Input>
          {masterError === undefined ? null : (
            <span className="input-error-message">{masterError}</span>
          )}
        </FormGroup>
      );
    }

    return (
      <MasterNumber
        id={masterId}
        label={masterLabelNode}
        value={allSame ? Number(values[0]) : null}
        placeholder={mixed ? mixedLabel : undefined}
        mixed={mixed}
        error={masterError}
        disabled={disabled}
        onChange={(value) => broadcast({ value })}
      />
    );
  };

  const renderVariantInput = (variant: WoodyVariant) => {
    if (type === 'checkbox') {
      return (
        <CheckboxInputField
          id={variant.name}
          label={variant.label}
          name={variant.name}
          onChange={onChange}
          value={Boolean(variant.value)}
          disabled={disabled}
          required
        />
      );
    }

    if (type === 'select') {
      return (
        <SelectInputFieldControlled
          id={variant.name}
          label={variant.label}
          name={variant.name}
          onChange={onChange}
          options={options}
          value={variant.value}
          disabled={disabled}
          required
        />
      );
    }

    return (
      <NumberInputField
        id={variant.name}
        label={variant.label}
        name={variant.name}
        onChange={onChange}
        value={Number(variant.value)}
        error={variant.error}
        disabled={disabled}
        required
      />
    );
  };

  return (
    <div
      className={`woody-master-field woody-master-field--${type}${
        mixed ? ' woody-master-field--mixed' : ''
      }`}
    >
      <div className="woody-master-field-header">
        <div className="woody-master-field-input">{renderMasterInput()}</div>
        <img
          className={`woody-master-field-chevron woody-master-field-chevron--${type}`}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          draggable={false}
          src={expanded ? chevronUp : chevronDown}
          alt={expanded ? 'collapse-icon' : 'expand-icon'}
          onClick={() => setExpanded((previous) => !previous)}
        />
      </div>
      {expanded ? (
        <div className="woody-master-field-variants">
          {variants.map((variant) => (
            <div key={variant.name}>{renderVariantInput(variant)}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const MasterNumber = ({
  id,
  label,
  value,
  placeholder,
  mixed,
  error,
  disabled,
  onChange,
}: {
  id: string;
  label: React.ReactNode;
  value: number | null;
  placeholder?: string;
  mixed?: boolean;
  error?: string;
  disabled?: boolean;
  onChange: (newValue: number) => void;
}): JSX.Element => {
  const [displayValue, setDisplayValue] = useState(
    value === null ? '' : value.toString()
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Ignore external value updates while a local edit is still pending,
    // otherwise the field flashes the old value when the debounce flushes.
    if (debounceRef.current) {
      return;
    }

    setDisplayValue(value === null ? '' : value.toString());
  }, [value]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  const emitChange = (newValue: number) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = undefined;
      onChange(newValue);
    }, 300);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setDisplayValue(newValue);

    if (newValue === '' || Number.isNaN(Number(newValue))) {
      emitChange(0);
      return;
    }

    emitChange(parseInt(newValue, 10));
  };

  return (
    <FormGroup>
      <Label className="label" for={id}>
        {label}
      </Label>
      <Input
        className={`text-field${mixed ? ' text-field--mixed' : ''}`}
        id={id}
        type="text"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
      />
      {error === undefined ? null : (
        <span className="input-error-message">{error}</span>
      )}
    </FormGroup>
  );
};

const MasterCheckbox = ({
  id,
  label,
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  id: string;
  label: React.ReactNode;
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  onChange: (isChecked: boolean) => void;
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current !== null) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <FormGroup className="checkbox-form-group">
      <Input
        checked={checked}
        className="checkbox-input"
        id={id}
        innerRef={inputRef}
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
        disabled={disabled}
      />
      <Label className="label" for={id}>
        {label}
      </Label>
    </FormGroup>
  );
};

export default WoodyMasterField;
